import { NgZone } from '@angular/core';
import {
  BehaviorSubject,
  Subject,
  animationFrames,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

export type Column = {
  key: string;
  label: string;
  width: string;
  smallSpacing?: boolean;
};

export class ColumnResizeManager {
  columnWidths$: BehaviorSubject<Record<string, string>>;
  resizingColumn: { key: string; startX: number; startWidth: number } | null =
    null;

  destroyed$ = new Subject<void>();
  private resizeMoveEvents$ = new Subject<MouseEvent>();

  // Global stylesheet for column widths (non-encapsulated)
  private styleSheet: CSSStyleSheet | null = null;
  private styleElement: HTMLStyleElement | null = null;

  updateColumnWithsEffect$ = this.resizeMoveEvents$.pipe(
    switchMap((event) =>
      animationFrames().pipe(
        take(1),
        tap(() => {
          if (!this.resizingColumn) return;

          const deltaX = event.clientX - this.resizingColumn.startX;
          const newWidth = Math.max(
            50,
            this.resizingColumn.startWidth + deltaX,
          );

          const currentWidths = this.columnWidths$.value;
          const updatedWidths = {
            ...currentWidths,
            [this.resizingColumn.key]: `${newWidth}px`,
          };
          this.columnWidths$.next(updatedWidths);
          this.saveColumnWidths(updatedWidths);
          // Update stylesheet immediately for real-time visual feedback
          this.updateStylesheetWidth(this.resizingColumn.key, newWidth);
        }),
      ),
    ),
    takeUntil(this.destroyed$),
  );

  constructor(private localStorageKey: string, private ngZone: NgZone) {
    this.columnWidths$ = new BehaviorSubject<Record<string, string>>(
      this.loadColumnWidths(),
    );
    this.initializeGlobalStylesheet();
    this.applyInitialWidths();
    this.updateColumnWithsEffect$.subscribe();
  }

  private initializeGlobalStylesheet(): void {
    // Check if stylesheet already exists for this localStorage key
    const existingStyle = document.querySelector(
      `style[data-column-resize-styles="${this.localStorageKey}"]`,
    ) as HTMLStyleElement;

    if (existingStyle) {
      this.styleElement = existingStyle;
      this.styleSheet = existingStyle.sheet as CSSStyleSheet;
    } else {
      // Create a non-encapsulated style element in the document head
      this.styleElement = document.createElement('style');
      this.styleElement.setAttribute(
        'data-column-resize-styles',
        this.localStorageKey,
      );
      document.head.appendChild(this.styleElement);
      this.styleSheet = this.styleElement.sheet as CSSStyleSheet;
    }

    // Ensure stylesheet is accessible
    if (!this.styleSheet) {
      console.error('Failed to initialize stylesheet for column resizing');
    }
  }

  private applyInitialWidths(): void {
    const widths = this.columnWidths$.value;
    Object.entries(widths).forEach(([key, width]) => {
      // Ensure CSS rules exist before updating width
      this.ensureColumnRulesExist(key);
      this.updateStylesheetWidth(key, this.parseWidthToPixels(width));
    });
  }

  private parseWidthToPixels(width: string): number {
    const match = width.match(/(\d+(?:\.\d+)?)px/);
    return match ? parseFloat(match[1]) : 150;
  }

  private updateStylesheetWidth(columnKey: string, width: number): void {
    if (!this.styleSheet) {
      console.warn('Stylesheet not initialized');
      return;
    }

    const propertyName = `--column-width-${columnKey}`;
    const propertyValue = `${width}px`;

    // Ensure :root rule exists
    let rootRule: CSSStyleRule | null = null;
    for (let i = 0; i < this.styleSheet.cssRules.length; i++) {
      const rule = this.styleSheet.cssRules[i];
      if (rule instanceof CSSStyleRule && rule.selectorText === ':root') {
        rootRule = rule;
        break;
      }
    }

    if (!rootRule) {
      try {
        const index = this.styleSheet.insertRule(
          ':root {}',
          this.styleSheet.cssRules.length,
        );
        rootRule = this.styleSheet.cssRules[index] as CSSStyleRule;
      } catch (e) {
        console.error('Failed to create :root rule:', e);
        return;
      }
    }

    // Update the custom property value
    rootRule.style.setProperty(propertyName, propertyValue);

    // Ensure CSS rules exist for this column key (create if they don't exist)
    this.ensureColumnRulesExist(columnKey);
  }

  private ensureColumnRulesExist(columnKey: string): void {
    if (!this.styleSheet) {
      // Retry initialization if stylesheet is not ready
      this.initializeGlobalStylesheet();
      if (!this.styleSheet) {
        console.warn('Stylesheet not available for column rules:', columnKey);
        return;
      }
    }

    const headerSelector = `.job-list-view-header-column[data-column-key="${columnKey}"]`;
    const itemSelector = `.compact-job-list-item-column[data-column-key="${columnKey}"]`;

    // Check if header rule exists
    let headerRuleExists = false;
    let itemRuleExists = false;
    let headerRuleIndex = -1;
    let itemRuleIndex = -1;

    for (let i = 0; i < this.styleSheet.cssRules.length; i++) {
      const rule = this.styleSheet.cssRules[i];
      if (rule instanceof CSSStyleRule) {
        if (rule.selectorText === headerSelector) {
          headerRuleExists = true;
          headerRuleIndex = i;
        }
        if (rule.selectorText === itemSelector) {
          itemRuleExists = true;
          itemRuleIndex = i;
        }
      }
    }

    // Create or update header rule
    if (!headerRuleExists) {
      const ruleText = `${headerSelector} { width: var(--column-width-${columnKey}, 150px) !important; }`;
      try {
        this.styleSheet.insertRule(ruleText, this.styleSheet.cssRules.length);
      } catch (e) {
        console.warn('Failed to insert header CSS rule:', e, ruleText);
      }
    } else if (headerRuleIndex >= 0) {
      // Update existing rule to ensure it has the correct property
      const rule = this.styleSheet.cssRules[headerRuleIndex] as CSSStyleRule;
      rule.style.setProperty(
        'width',
        `var(--column-width-${columnKey}, 150px)`,
        'important',
      );
    }

    // Create or update item rule
    if (!itemRuleExists) {
      const ruleText = `${itemSelector} { width: var(--column-width-${columnKey}, 150px) !important; }`;
      try {
        this.styleSheet.insertRule(ruleText, this.styleSheet.cssRules.length);
      } catch (e) {
        console.warn('Failed to insert item CSS rule:', e, ruleText);
      }
    } else if (itemRuleIndex >= 0) {
      // Update existing rule to ensure it has the correct property
      const rule = this.styleSheet.cssRules[itemRuleIndex] as CSSStyleRule;
      rule.style.setProperty(
        'width',
        `var(--column-width-${columnKey}, 150px)`,
        'important',
      );
    }
  }

  loadColumnWidths(): Record<string, string> {
    try {
      const saved = localStorage.getItem(this.localStorageKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  }

  saveColumnWidths(widths: Record<string, string>): void {
    localStorage.setItem(this.localStorageKey, JSON.stringify(widths));
  }

  // Public method for components to ensure CSS rules exist for their columns
  ensureColumnKeysExist(columns: Array<{ key: string; width: string }>): void {
    columns.forEach((column) => {
      // Set initial width in :root if not already set
      const savedWidths = this.columnWidths$.value;
      const widthToUse = savedWidths[column.key] || column.width;
      const widthInPixels = this.parseWidthToPixels(widthToUse);

      // Ensure :root has the property set
      this.ensureRootPropertyExists(column.key, widthInPixels);

      // Ensure CSS rules exist
      this.ensureColumnRulesExist(column.key);
    });
  }

  private ensureRootPropertyExists(columnKey: string, width: number): void {
    if (!this.styleSheet) {
      // Retry initialization if stylesheet is not ready
      this.initializeGlobalStylesheet();
      if (!this.styleSheet) {
        console.warn('Stylesheet not available for column:', columnKey);
        return;
      }
    }

    const propertyName = `--column-width-${columnKey}`;

    // Find or create :root rule
    let rootRule: CSSStyleRule | null = null;
    for (let i = 0; i < this.styleSheet.cssRules.length; i++) {
      const rule = this.styleSheet.cssRules[i];
      if (rule instanceof CSSStyleRule && rule.selectorText === ':root') {
        rootRule = rule;
        break;
      }
    }

    if (!rootRule) {
      try {
        const index = this.styleSheet.insertRule(
          ':root {}',
          this.styleSheet.cssRules.length,
        );
        rootRule = this.styleSheet.cssRules[index] as CSSStyleRule;
      } catch (e) {
        console.error('Failed to create :root rule:', e);
        return;
      }
    }

    // Always set/update the property to ensure it has the correct value
    rootRule.style.setProperty(propertyName, `${width}px`);
  }

  onResizeStart(event: MouseEvent, columnKey: string): void {
    event.preventDefault();
    const target = event.target as HTMLElement;
    const columnHeader = target.closest(
      '.job-list-view-header-column',
    ) as HTMLElement;
    if (!columnHeader) return;

    const currentWidth = columnHeader.offsetWidth;
    this.resizingColumn = {
      key: columnKey,
      startX: event.clientX,
      startWidth: currentWidth,
    };

    // Run resize handlers outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onResizeMove);
      document.addEventListener('mouseup', this.onResizeEnd);
    });

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  onResizeMove = (event: MouseEvent): void => {
    if (!this.resizingColumn) return;

    // Emit the event to the observable for processing
    // The effect will handle throttling via animationFrames
    this.resizeMoveEvents$.next(event);
  };

  onResizeEnd = (event: MouseEvent): void => {
    if (!this.resizingColumn) return;

    const deltaX = event.clientX - this.resizingColumn.startX;
    const newWidth = Math.max(50, this.resizingColumn.startWidth + deltaX);

    // Ensure stylesheet is updated with final width
    this.updateStylesheetWidth(this.resizingColumn.key, newWidth);

    // Update the BehaviorSubject and save to localStorage
    this.ngZone.run(() => {
      const currentWidths = this.columnWidths$.value;
      const updatedWidths = {
        ...currentWidths,
        [this.resizingColumn!.key]: `${newWidth}px`,
      };
      this.columnWidths$.next(updatedWidths);
      this.saveColumnWidths(updatedWidths);
    });

    this.resizingColumn = null;

    // Remove global event listeners
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  cleanup(): void {
    // Clean up resize event listeners if still active
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);

    // Complete the destroyed$ subject to clean up subscriptions
    this.destroyed$.next();
    this.destroyed$.complete();

    // Remove the global stylesheet
    if (this.styleElement && this.styleElement.parentNode) {
      this.styleElement.parentNode.removeChild(this.styleElement);
    }
    this.styleElement = null;
    this.styleSheet = null;
  }
}

export const defaultDispoJobColumns: Column[] = [
  { key: 'status', label: 'Status', width: '268px' },
  { key: 'shift', label: 'Schicht', width: '175px' },
  { key: 'department', label: 'Abteilung', width: '150px' },
  {
    key: 'organisation',
    label: 'Organisation',
    width: '125px',
    smallSpacing: true,
  },
  {
    key: 'bookedInvitees',
    label: 'Gebuchte Mitarbeiter',
    width: '240px',
  },
  { key: 'transfers', label: 'Transfer', width: '422px' },
  { key: 'jobType', label: 'Diensttyp', width: '132px' },
  { key: 'caseNumberShort', label: 'Fallnr.', width: '72px' },
  { key: 'description', label: 'Beschreibung', width: '200px' },
  { key: 'certificate', label: 'Qualifikation', width: '210px' },
  { key: 'createdBy', label: 'Erstellt von', width: '160px' },
  { key: 'createdAt', label: 'Erstellt am', width: '160px' },
  { key: 'updatedAt', label: 'Aktualisiert am', width: '160px' },
];

export const optionalJobColumns = {
  internalNote: {
    key: 'internalNote',
    label: 'Interner Hinweis',
    width: '200px',
  },
  demandReason: { key: 'demandReason', label: 'Bedarf', width: '132px' },
} as const;

export const defaultPoolerJobColumns: Column[] = [
  { key: 'shift', label: 'Schicht', width: '175px' },
  { key: 'department', label: 'Abteilung', width: '150px' },
  {
    key: 'organisation',
    label: 'Organisation',
    width: '125px',
    smallSpacing: true,
  },
  { key: 'address', label: 'Adresse', width: '272px' },
  { key: 'jobType', label: 'Diensttyp', width: '132px' },
  { key: 'description', label: 'Beschreibung', width: '200px' },
  { key: 'certificate', label: 'Qualifikation', width: '210px' },
  { key: 'offerFrom', label: 'Angebot von', width: '160px' },
];

export const defaultJobRequestColumns: Column[] = [
  { key: 'status', label: 'Status', width: '240px' },
  { key: 'shift', label: 'Schicht', width: '175px' },
  { key: 'bookedEmployee', label: 'Gebuchte Mitarbeiter', width: '200px' },
  { key: 'jobType', label: 'Diensttyp', width: '132px' },
  { key: 'demandReason', label: 'Bedarf', width: '150px' },
  { key: 'createdAt', label: 'Erstellt am', width: '180px' },
  { key: 'createdBy', label: 'Erstellt von', width: '180px' },
];
